import { Card, Field, Input } from "@dl/web-core-js/ui";
import { useTranslation } from "react-i18next";
import type { ProductDTO, ProductDescriptionDTO } from "../../api/product";

type LanguageKey = "description_en" | "description_fr";

const FEATURES = ["feature1", "feature2", "feature3", "feature4", "feature5"] as const;

/**
 * Product copy, English and French side by side.
 *
 * Name is the one required field — every retailer feed leads with it. The five
 * feature lines are the bullet points those feeds turn into "about this item"
 * lists, which is why they are separate fields rather than one textarea.
 */
export function DescriptionsTab({
  draft,
  onChange,
  readOnly,
}: {
  draft: ProductDTO;
  onChange: (next: ProductDTO) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();

  const languages: Array<{ key: LanguageKey; label: string; language: string }> = [
    { key: "description_en", label: t("product.language.en", "English"), language: "ENGLISH" },
    { key: "description_fr", label: t("product.language.fr", "French"), language: "FRENCH" },
  ];

  const set = (key: LanguageKey, language: string, patch: Partial<ProductDescriptionDTO>) =>
    onChange({ ...draft, [key]: { ...(draft[key] ?? { language }), ...patch } });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {languages.map(({ key, label, language }) => {
        const description = draft[key];
        const nameMissing = !description?.product_name?.trim();

        return (
          <Card key={key} className="p-5">
            <h3 className="mb-4 text-[13px] font-semibold text-ink">{label}</h3>
            <div className="flex flex-col gap-4">
              <Field
                label={t("column.name", "Name")}
                htmlFor={`${key}-name`}
                required
                error={nameMissing ? t("form.validation.field.required", "Required") : undefined}
              >
                <Input
                  id={`${key}-name`}
                  value={description?.product_name ?? ""}
                  readOnly={readOnly}
                  invalid={nameMissing}
                  onChange={(event) => set(key, language, { product_name: event.target.value })}
                />
              </Field>

              <Field label={t("product.short-description", "Short description")} htmlFor={`${key}-short`}>
                <textarea
                  id={`${key}-short`}
                  rows={2}
                  value={description?.short_description ?? ""}
                  readOnly={readOnly}
                  onChange={(event) => set(key, language, { short_description: event.target.value })}
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink"
                />
              </Field>

              <Field label={t("product.long-description", "Long description")} htmlFor={`${key}-long`}>
                <textarea
                  id={`${key}-long`}
                  rows={6}
                  value={description?.long_description ?? ""}
                  readOnly={readOnly}
                  onChange={(event) => set(key, language, { long_description: event.target.value })}
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink"
                />
              </Field>

              <div className="flex flex-col gap-2 border-t border-line-soft pt-3">
                {FEATURES.map((feature, index) => (
                  <Field
                    key={feature}
                    label={t("product.feature", "Feature {{n}}", { n: index + 1 })}
                    htmlFor={`${key}-${feature}`}
                  >
                    <Input
                      id={`${key}-${feature}`}
                      value={description?.[feature] ?? ""}
                      readOnly={readOnly}
                      onChange={(event) => set(key, language, { [feature]: event.target.value })}
                    />
                  </Field>
                ))}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
